// API utility functions for making authenticated requests

import { getApiBaseUrl } from '../config/api';

export interface ApiResponse<T> {
  data?: T;
  error?: string;
}

/**
 * Get the authentication token from localStorage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

/**
 * Get the current user from localStorage
 */
export const getCurrentUser = (): { id: number; email: string } | null => {
  const userStr = localStorage.getItem('user');
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return getAuthToken() !== null;
};

/**
 * Clear authentication data (and client selection so next user doesn't see previous user's client)
 */
export const clearAuth = (): void => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  localStorage.removeItem('selectedClientName');
};

/**
 * Make an authenticated API request
 */
export const apiRequest = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const token = getAuthToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Get API base URL dynamically to ensure it's computed at request time
  const apiBaseUrl = getApiBaseUrl();

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (err: any) {
    const msg = err?.message || String(err);
    const isNetworkError =
      msg === 'Failed to fetch' ||
      msg.includes('Load failed') ||
      msg.includes('NetworkError') ||
      msg.includes('Network request failed') ||
      (typeof navigator !== 'undefined' && !navigator.onLine);
    if (isNetworkError) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw err;
  }

  // If unauthorized, clear auth and throw error.
  // Do NOT clear auth on 403 (permission errors), otherwise users get logged out
  // for valid access-denied responses and see misleading "Authentication required" follow-ups.
  if (response.status === 401) {
    clearAuth();
    const error = await response.json().catch(() => ({ error: 'Invalid or expired token' }));
    throw new Error(error.error || 'Invalid or expired token');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: <T = any>(endpoint: string, options?: RequestInit) => 
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),
  
  post: <T = any>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),
  
  put: <T = any>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T = any>(endpoint: string, data?: any, options?: RequestInit) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      // Use nullish check so JSON bodies preserve `false` (truthy object with false fields is fine; plain `false` payload would still skip — callers should pass an object).
      body: data !== undefined && data !== null ? JSON.stringify(data) : undefined,
    }),
  
  delete: <T = any>(endpoint: string, options?: RequestInit) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};

